export type EventType =
  | 'zugegangen'
  | 'verkauft'
  | 'zurueckgegeben'
  | 'empfehlung'
  | 'werkstatt'
  | 'hu_faellig';

export interface FahrzeugForTimeline {
  id: string;
  marke: string;
  modell: string;
  variante?: string;
  kennzeichen?: string;
  betrieb: string;
  status: string;
  created_at: string;
  anschaffungsdatum?: string;
  verkauft_am?: string;
  verkauft_preis_brutto?: number;
  zurueckgegeben_am?: string;
  hu_naechste?: string;
  naechste_hu?: string; // legacy alias
}

export interface TimelineEvent {
  id: string;
  type: EventType;
  date: Date;
  fahrzeugId: string;
  fahrzeugLabel: string;
  kennzeichen?: string;
  beschreibung: string;
  betrieb: string;
  wert?: number;
}

export interface TimelineFilter {
  betrieb: string | null;
  typen: EventType[];
  von?: Date;
  bis?: Date;
}

const MONTHS_24 = 24;
const HU_WARNING_DAYS = 60;

function label(fz: FahrzeugForTimeline): string {
  return [fz.marke, fz.modell, fz.variante, fz.kennzeichen].filter(Boolean).join(' ');
}

function parseDate(s: string): Date {
  // Accept ISO dates (YYYY-MM-DD) and ISO datetimes
  return new Date(s.includes('T') ? s : s + 'T00:00:00Z');
}

function cutoff(now: Date): Date {
  const d = new Date(now);
  d.setMonth(d.getMonth() - MONTHS_24);
  return d;
}

export function aggregateFahrzeugEvents(
  fahrzeuge: FahrzeugForTimeline[],
  now: Date = new Date(),
): TimelineEvent[] {
  const earliest = cutoff(now);
  const huDeadline = new Date(now);
  huDeadline.setDate(huDeadline.getDate() + HU_WARNING_DAYS);

  const events: TimelineEvent[] = [];

  for (const fz of fahrzeuge) {
    const lab = label(fz);

    // — Event: Fahrzeug zugegangen —
    // Prefer anschaffungsdatum; fall back to created_at
    const zugegangDatStr = fz.anschaffungsdatum ?? fz.created_at;
    const zugegangDat = parseDate(zugegangDatStr);
    if (zugegangDat >= earliest) {
      events.push({
        id: `${fz.id}-zugegangen`,
        type: 'zugegangen',
        date: zugegangDat,
        fahrzeugId: fz.id,
        fahrzeugLabel: lab,
        kennzeichen: fz.kennzeichen,
        beschreibung: 'Fahrzeug zugegangen',
        betrieb: fz.betrieb,
      });
    }

    // — Event: Fahrzeug verkauft —
    if (fz.verkauft_am) {
      const d = parseDate(fz.verkauft_am);
      if (d >= earliest) {
        events.push({
          id: `${fz.id}-verkauft`,
          type: 'verkauft',
          date: d,
          fahrzeugId: fz.id,
          fahrzeugLabel: lab,
          kennzeichen: fz.kennzeichen,
          beschreibung: `Fahrzeug verkauft${fz.verkauft_preis_brutto ? ` · ${fz.verkauft_preis_brutto.toLocaleString('de-DE')} €` : ''}`,
          betrieb: fz.betrieb,
          wert: fz.verkauft_preis_brutto,
        });
      }
    }

    // — Event: Fahrzeug zurückgegeben —
    if (fz.zurueckgegeben_am) {
      const d = parseDate(fz.zurueckgegeben_am);
      if (d >= earliest) {
        events.push({
          id: `${fz.id}-zurueckgegeben`,
          type: 'zurueckgegeben',
          date: d,
          fahrzeugId: fz.id,
          fahrzeugLabel: lab,
          kennzeichen: fz.kennzeichen,
          beschreibung: 'Fahrzeug zurückgegeben',
          betrieb: fz.betrieb,
        });
      }
    }

    // — Event: In Werkstatt (proxy: current status; no change-tracking table yet) —
    if (fz.status === 'werkstatt') {
      const d = parseDate(fz.created_at);
      if (d >= earliest) {
        events.push({
          id: `${fz.id}-werkstatt`,
          type: 'werkstatt',
          date: d,
          fahrzeugId: fz.id,
          fahrzeugLabel: lab,
          kennzeichen: fz.kennzeichen,
          beschreibung: 'In Werkstatt',
          betrieb: fz.betrieb,
        });
      }
    }

    // — Event: HU fällig (due within 30 days, or overdue) —
    const huStr = fz.hu_naechste ?? fz.naechste_hu;
    if (huStr) {
      const huDat = parseDate(huStr);
      if (huDat <= huDeadline && huDat >= earliest) {
        events.push({
          id: `${fz.id}-hu_faellig`,
          type: 'hu_faellig',
          date: huDat,
          fahrzeugId: fz.id,
          fahrzeugLabel: lab,
          kennzeichen: fz.kennzeichen,
          beschreibung: `HU fällig am ${huDat.toLocaleDateString('de-DE')}`,
          betrieb: fz.betrieb,
        });
      }
    }
  }

  // Sort descending (newest first)
  events.sort((a, b) => b.date.getTime() - a.date.getTime());
  return events;
}

export function filterEvents(events: TimelineEvent[], filter: TimelineFilter): TimelineEvent[] {
  return events.filter((ev) => {
    if (filter.betrieb && ev.betrieb !== filter.betrieb) return false;
    if (filter.typen.length > 0 && !filter.typen.includes(ev.type)) return false;
    if (filter.von && ev.date < filter.von) return false;
    if (filter.bis && ev.date > filter.bis) return false;
    return true;
  });
}

const CSV_HEADER = 'Datum,Fahrzeug,Kennzeichen,Ereignis-Typ,Wert/Beschreibung,Betrieb';

const EVENT_TYPE_LABEL: Record<EventType, string> = {
  zugegangen: 'Fahrzeug zugegangen',
  verkauft: 'Fahrzeug verkauft',
  zurueckgegeben: 'Fahrzeug zurückgegeben',
  empfehlung: 'Verkaufs-Empfehlung',
  werkstatt: 'In Werkstatt',
  hu_faellig: 'HU fällig',
};

function csvCell(s: string | undefined): string {
  const v = s ?? '';
  return v.includes(',') || v.includes('"') || v.includes('\n')
    ? `"${v.replace(/"/g, '""')}"`
    : v;
}

export function eventsToCSV(events: TimelineEvent[]): string {
  const rows = events.map((ev) => {
    return [
      ev.date.toLocaleDateString('de-DE'),
      ev.fahrzeugLabel,
      ev.kennzeichen ?? '',
      EVENT_TYPE_LABEL[ev.type] ?? ev.type,
      ev.wert != null ? `${ev.wert.toLocaleString('de-DE')} €` : ev.beschreibung,
      ev.betrieb,
    ]
      .map(csvCell)
      .join(',');
  });
  return [CSV_HEADER, ...rows].join('\n');
}
